import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { api } from './services/api.js';
import {
  Kelas,
  Siswa,
  TahunAjaran,
  AkunKeuangan,
  PosTagihan,
  TagihanSiswa,
  SettingTahunAjaran,
  TransaksiPembayaran,
  SppGradeRule,
  UserPrivilege,
  BeasiswaPotongan,
} from './types/index.js';
import { useDialog } from './components/ui/DialogProvider.js';

import { Header } from './components/Header.js';
import { Sidebar } from './components/Sidebar.js';
import { DashboardHomeView } from './components/DashboardHomeView.js';
import { KelasView } from './components/data-awal/KelasView.js';
import { SiswaView } from './components/data-awal/SiswaView.js';
import { TahunAjaranView } from './components/data-awal/TahunAjaranView.js';
import { AkunDetailView } from './components/data-awal/AkunDetailView.js';
import { PosTagihanView } from './components/data-awal/PosTagihanView.js';
import { TagihanSiswaView } from './components/data-awal/TagihanSiswaView.js';
import { SettingTahunAjaranView } from './components/data-awal/SettingTahunAjaranView.js';
import { RecurringSppBillingView } from './components/data-awal/RecurringSppBillingView.js';
import { BeasiswaView } from './components/data-awal/BeasiswaView.js';

import { LoketPembayaranView } from './components/transaksi/LoketPembayaranView.js';
import { CetakBuktiCancelView } from './components/transaksi/CetakBuktiCancelView.js';
import { SaldoSiswaView } from './components/transaksi/SaldoSiswaView.js';
import { TransaksiSiswaView } from './components/transaksi/TransaksiSiswaView.js';
import { MutasiTabunganView } from './components/transaksi/MutasiTabunganView.js';
import { DashboardTabunganView } from './components/transaksi/DashboardTabunganView.js';
import { DashboardEWalletView } from './components/transaksi/DashboardEWalletView.js';

import { ReportView } from './components/report/ReportView.js';
import { JurnalAkuntansiView } from './components/accounting/JurnalAkuntansiView.js';
import { UtilityView } from './components/utility/UtilityView.js';
import { GatewayConsoleView } from './components/gateway/GatewayConsoleView.js';
import { ExpoMobileSimulator } from './components/expo/ExpoMobileSimulator.js';
import { ExpoCodeView } from './components/expo/ExpoCodeView.js';
import { MobileLandingView } from './components/expo/MobileLandingView.js';
import { LoginView } from './components/auth/LoginView.js';
import { ReceiptModal } from './components/ReceiptModal.js';
import { PortalLayout } from './components/portal/PortalLayout.js';
import { DocumentationView } from './components/docs/DocumentationView.js';

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert, showConfirm } = useDialog();
  // App Mode State
  const [appMode, setAppMode] = useState<'WEB_PORTAL' | 'EXPO_SIMULATOR' | 'GATEWAY_TESTER' | 'EXPO_CODE'>('WEB_PORTAL');

  // Application Data States
  const [kelasList, setKelasList] = useState<Kelas[]>([]);
  const [siswaList, setSiswaList] = useState<Siswa[]>([]);
  const [tahunAjaranList, setTahunAjaranList] = useState<TahunAjaran[]>([]);
  const [akunList, setAkunList] = useState<AkunKeuangan[]>([]);
  const [posList, setPosList] = useState<PosTagihan[]>([]);
  const [beasiswaList, setBeasiswaList] = useState<BeasiswaPotongan[]>([]);
  const [tagihanList, setTagihanList] = useState<TagihanSiswa[]>([]);
  const [sppRules, setSppRules] = useState<SppGradeRule[]>([]);
  const [setting, setSetting] = useState<SettingTahunAjaran | null>(null);
  const [transaksiList, setTransaksiList] = useState<TransaksiPembayaran[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [userPrivilegeList, setUserPrivilegeList] = useState<UserPrivilege[]>([]);

  // Active Receipt Modal
  const [activeReceipt, setActiveReceipt] = useState<TransaksiPembayaran | null>(null);
  const [loading, setLoading] = useState(true);

  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('sdmt_token');
    return Boolean(token && localStorage.getItem('isAuthenticated') === 'true');
  });
  const [currentUser, setCurrentUser] = useState<any>(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('sdmt_token');
    if (!token) return null;
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    const handleUnauthorized = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('sdmt_token');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('isAuthenticated');
      setIsAuthenticated(false);
      setCurrentUser(null);
    };
    window.addEventListener('auth-unauthorized', handleUnauthorized);
    window.addEventListener('auth-expired', handleUnauthorized);
    return () => {
      window.removeEventListener('auth-unauthorized', handleUnauthorized);
      window.removeEventListener('auth-expired', handleUnauthorized);
    };
  }, []);

  const handleLogin = (user: any, token?: string) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('currentUser', JSON.stringify(user));
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('sdmt_token', token);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('token');
    localStorage.removeItem('sdmt_token');
    showAlert('Berhasil Logout!', { type: 'success' });
  };

  // Load all initial data from modular API
  const loadAllData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const isSuperadmin = currentUser?.levelUser === 'SUPERADMIN';
      const isAdminKeuangan = currentUser?.levelUser === 'ADMIN_KEUANGAN';
      const canViewPrivileges = isSuperadmin;
      const canViewStats = isSuperadmin || isAdminKeuangan || currentUser?.levelUser === 'KEPALA_SEKOLAH' || currentUser?.levelUser === 'KASIR_LOKET';

      const [
        kData,
        sData,
        tData,
        aData,
        pData,
        beasiswaData,
        tagihanData,
        rulesData,
        settingData,
        trxData,
        statsData,
        userPrivilegeData,
      ] = await Promise.all([
        api.getKelas().catch(() => []),
        api.getSiswa().catch(() => []),
        api.getTahunAjaran().catch(() => []),
        api.getAkun().catch(() => []),
        api.getPosTagihan().catch(() => []),
        api.getBeasiswa().catch(() => []),
        api.getAllTagihan().catch(() => []),
        api.getSppRules().catch(() => []),
        api.getSettingTahunAjaran().catch(() => null),
        api.getTransaksi().catch(() => []),
        canViewStats ? api.getDashboardStats().catch(() => null) : Promise.resolve(null),
        canViewPrivileges ? api.getUserPrivileges().catch(() => []) : Promise.resolve([]),
      ]);

      setKelasList(kData);
      setSiswaList(sData);
      setTahunAjaranList(tData);
      setAkunList(aData);
      setPosList(pData);
      setBeasiswaList(beasiswaData);
      setTagihanList(tagihanData);
      setSppRules(rulesData || []);
      setSetting(settingData);
      setTransaksiList(trxData);
      setStats(statsData);
      setUserPrivilegeList(userPrivilegeData || []);
    } catch (err) {
      console.error('Failed to load school data:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated]);

  // Handlers for Kelas
  const handleAddKelas = async (data: Partial<Kelas>) => {
    await api.createKelas(data);
    await loadAllData();
  };

  const handleUpdateKelas = async (id: string, data: Partial<Kelas>) => {
    await api.updateKelas(id, data);
    await loadAllData();
  };

  const handleDeleteKelas = async (id: string) => {
    await api.deleteKelas(id);
    await loadAllData();
  };

  // Handlers for Siswa
  const handleAddSiswa = async (data: Partial<Siswa>) => {
    await api.createSiswa(data);
    await loadAllData();
  };

  const handleUpdateSiswa = async (id: string, data: Partial<Siswa>) => {
    await api.updateSiswa(id, data);
    await loadAllData();
  };

  const handleResetApk = async (id: string) => {
    await api.resetUserApk(id);
    showAlert('PIN login Aplikasi Mobile Siswa berhasil di-reset ke default: 123456', { type: 'success' });
    await loadAllData();
  };

  const [isSyncingRfid, setIsSyncingRfid] = useState(false);

  const handleSyncRfid = async () => {
    try {
      setIsSyncingRfid(true);
      const res = await api.syncRfidAttendance({
        deviceIp: '192.168.1.201',
      });
      if (res.success) {
        if (res.siswaList) {
          setSiswaList(res.siswaList);
        } else {
          await loadAllData();
        }
        showAlert(
          `Berhasil menarik data presensi dari Mesin RFID Solution X107!\n` +
          `- Total Profil Tersinkron: ${res.data?.totalSynced || siswaList.length} siswa\n` +
          `- Siswa Kehadiran Rendah (<80%): ${res.data?.lowAttendanceCount || 0} siswa`,
          { type: 'success', title: 'Sinkronisasi Berhasil' }
        );
      }
    } catch (err: any) {
      showAlert('Gagal sinkronisasi mesin RFID: ' + (err?.message || 'Koneksi timeout'), { type: 'danger', title: 'Gagal' });
    } finally {
      setIsSyncingRfid(false);
    }
  };

  const handleUpdateAttendance = async (nis: string, data: any) => {
    await api.updateStudentAttendance(nis, data);
    await loadAllData();
  };

  // Handlers for Tahun Ajaran
  const handleAddTahunAjaran = async (data: Partial<TahunAjaran>) => {
    await api.createTahunAjaran(data);
    await loadAllData();
  };

  // Handlers for Akun
  const handleAddAkun = async (data: Partial<AkunKeuangan>) => {
    await api.createAkun(data);
    await loadAllData();
  };
  const handleUpdateAkun = async (id: string, data: Partial<AkunKeuangan>) => {
    await api.updateAkun(id, data);
    await loadAllData();
  };
  const handleDeleteAkun = async (id: string) => {
    await api.deleteAkun(id);
    await loadAllData();
  };

  // Handlers for Pos Tagihan
  const handleAddPos = async (data: Partial<PosTagihan>) => {
    await api.createPosTagihan(data);
    await loadAllData();
  };

  const handleUpdatePos = async (id: string, data: Partial<PosTagihan>) => {
    await api.updatePosTagihan(id, data);
    await loadAllData();
  };

  // Handlers for Beasiswa
  const handleCreateBeasiswa = async (payload: any) => {
    await api.createBeasiswa(payload);
    await loadAllData();
  };
  const handleVerifikasiBeasiswa = async (id: string, payload: any) => {
    await api.verifyBeasiswa(id, payload);
    await loadAllData();
  };
  const handleBatchVerifikasiBeasiswa = async (payload: any) => {
    await api.batchVerifyBeasiswa(payload);
    await loadAllData();
  };
  const handleDeleteBeasiswa = async (id: string) => {
    await api.deleteBeasiswa(id);
    await loadAllData();
  };

  // Handlers for Tagihan
  const handleGenerateTagihan = async (payload: any) => {
    await api.generateTagihan(payload);
    await loadAllData();
    showAlert('Tagihan berhasil dibuat!', { type: 'success' });
  };

  const handleGenerateRecurringSpp = async (payload: any) => {
    const res = await api.generateRecurringSpp(payload);
    await loadAllData();
    return res;
  };

  const handleUpdateSppRule = async (id: string, data: Partial<SppGradeRule>) => {
    await api.updateSppRule(id, data);
    await loadAllData();
  };

  const handleCreateSppRule = async (data: Partial<SppGradeRule>) => {
    await api.createSppRule(data);
    await loadAllData();
  };

  const handleDeleteTagihan = async (id: string) => {
    await api.deleteTagihan(id);
    await loadAllData();
  };

  // Handlers for Setting
  const handleUpdateSetting = async (data: Partial<SettingTahunAjaran>) => {
    const updated = await api.updateSettingTahunAjaran(data);
    setSetting(updated);
    showAlert('Pengaturan tahun ajaran & lembaga berhasil diperbarui!', { type: 'success' });
  };

  // Handlers for Payment
  const handleProcessPayment = async (payload: any) => {
    const trx = await api.processPayment(payload);
    await loadAllData(true);
    return trx;
  };

  const handleCancelTransaksi = async (id: string, alasan: string) => {
    await api.cancelTransaksi(id, alasan);
    await loadAllData();
    showAlert(`Transaksi berhasil dibatalkan (void).`, { type: 'success' });
  };

  // Handlers for Tabungan
  const handleDepositOrWithdraw = async (payload: any) => {
    await api.createMutasiTabungan(payload);
    await loadAllData();
    showAlert(`Transaksi tabungan berhasil diproses!`, { type: 'success' });
  };

  // Handlers for Utility
  const handlePromoteClass = async (fromKelasId: string, toKelasId: string, studentIds?: string[]) => {
    const res = await api.promoteClass(fromKelasId, toKelasId, studentIds);
    await loadAllData();
    return res;
  };

  const handleGraduateClass = async (kelasId: string, studentIds?: string[]) => {
    const res = await api.graduateClass(kelasId, studentIds);
    await loadAllData();
    return res;
  };

  const handleAddUserPrivilege = async (data: Partial<UserPrivilege>) => {
    await api.createUserPrivilege(data);
    await loadAllData();
  };

  const handleUpdateUserPrivilege = async (id: string, data: Partial<UserPrivilege>) => {
    await api.updateUserPrivilege(id, data);
    await loadAllData();
  };

  const handleDeleteUserPrivilege = async (id: string) => {
    await api.deleteUserPrivilege(id);
    await loadAllData();
  };

  const isMobileLanding = location.pathname === '/mobile';
  const isPortal = location.pathname.startsWith('/portal');
  const isStudent = location.pathname.startsWith('/student');

  // Bypass admin authentication for landing page, portal, and student routes
  if (isMobileLanding) {
    return (
      <Routes>
        <Route path="/mobile" element={<MobileLandingView />} />
      </Routes>
    );
  }

  if (isPortal) {
    return (
      <Routes>
        <Route path="/portal/*" element={<PortalLayout />} />
      </Routes>
    );
  }

  // Also support the direct /expo-simulator link
  const isSimulator = location.pathname.startsWith('/expo-simulator');
  if (isSimulator) {
    return (
      <Routes>
        <Route path="/expo-simulator/*" element={<PortalLayout />} />
      </Routes>
    );
  }

  if (!isAuthenticated) {
    return <LoginView onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-slate-100 flex font-sans text-slate-800 overflow-hidden">
      {/* Sidebar Navigation (Left) */}
      <Sidebar appMode={appMode} currentUser={currentUser} />

      {/* Main Content Area (Right) */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-y-auto">
        {/* Navigation & Header */}
        <Header
          appMode={appMode}
          setAppMode={setAppMode}
          currentUser={currentUser}
          onLogout={() => showConfirm('Apakah Anda yakin ingin keluar dari sistem?', { title: 'Konfirmasi Logout', type: 'warning' }).then(ok => { if(ok) handleLogout(); })}
        />

        <main className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-slate-500">
            <div className="w-8 h-8 border-4 border-sky-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs font-semibold">Memuat data sistem pembayaran SDMT Ponorogo...</p>
          </div>
        ) : (
          <>
            {/* 1. EXPO MOBILE SIMULATOR MODE */}
            {appMode === 'EXPO_SIMULATOR' && (
              <ExpoMobileSimulator
                siswaList={siswaList}
                tagihanList={tagihanList}
                transaksiList={transaksiList}
                onProcessPayment={handleProcessPayment}
              />
            )}

            {/* 2. PAYMENT GATEWAY TESTER MODE */}
            {appMode === 'GATEWAY_TESTER' && <GatewayConsoleView />}

            {/* 3. EXPO CODE & ARCHITECTURE MODE */}
            {appMode === 'EXPO_CODE' && <ExpoCodeView />}

            {/* 4. WEB PORTAL / LOKET ADMIN MODE */}
            {appMode === 'WEB_PORTAL' && (
              <Routes>
                <Route path="/" element={
                  <DashboardHomeView
                    stats={stats}
                    transaksiList={transaksiList}
                    siswaList={siswaList}
                    tagihanList={tagihanList}
                    onNavigate={(menu) => {
                      const routes: Record<string, string> = {
                        'Kelas': '/kelas',
                        'Siswa': '/siswa',
                        'Tagihan': '/tagihan-siswa',
                        'Tagihan Siswa': '/tagihan-siswa',
                        'Pembayaran': '/loket',
                        'Loket Pembayaran': '/loket',
                        'Pos Tagihan': '/pos-tagihan',
                        'Payment Gateway': '/payment-gateway',
                        'Report Pembayaran': '/report/pembayaran',
                        'Report Tunggakan': '/report/tunggakan',
                        'Saldo Siswa': '/saldo-siswa',
                        'Cetak Bukti & Cancel': '/cetak-bukti',
                      };
                      if (routes[menu]) {
                        navigate(routes[menu]);
                      } else {
                        console.warn('Unknown menu for navigation:', menu);
                      }
                    }}
                    onShowReceipt={(trx) => setActiveReceipt(trx)}
                    onOpenMobile={() => setAppMode('EXPO_SIMULATOR')}
                    onSyncRfid={handleSyncRfid}
                    isSyncingRfid={isSyncingRfid}
                  />
                } />

                {/* Data Awal Sub-Menus */}
                <Route path="/kelas" element={
                  <KelasView
                    kelasList={kelasList}
                    onAddKelas={handleAddKelas}
                    onUpdateKelas={handleUpdateKelas}
                    onDeleteKelas={handleDeleteKelas}
                  />
                } />

                <Route path="/siswa" element={
                  <SiswaView
                    siswaList={siswaList}
                    kelasList={kelasList}
                    onAddSiswa={handleAddSiswa}
                    onUpdateSiswa={handleUpdateSiswa}
                    onResetApk={handleResetApk}
                    onUpdateAttendance={handleUpdateAttendance}
                    onSyncRfid={handleSyncRfid}
                    isSyncingRfid={isSyncingRfid}
                    onRefresh={loadAllData}
                  />
                } />

                <Route path="/tahun-ajaran" element={
                  <TahunAjaranView
                    tahunAjaranList={tahunAjaranList}
                    onAddTahunAjaran={handleAddTahunAjaran}
                  />
                } />

                <Route path="/akun" element={
                  <AkunDetailView 
                    akunList={akunList} 
                    onAddAkun={handleAddAkun} 
                    onUpdateAkun={handleUpdateAkun}
                    onDeleteAkun={handleDeleteAkun}
                  />
                } />

                <Route path="/pos-tagihan" element={
                  <PosTagihanView 
                    posTagihanList={posList} 
                    onAddPos={handleAddPos} 
                    onUpdatePos={handleUpdatePos}
                  />
                } />

                <Route path="/data-beasiswa" element={
                  <BeasiswaView 
                    siswaList={siswaList}
                    kelasList={kelasList}
                    posList={posList}
                    beasiswaList={beasiswaList}
                    sppRules={sppRules}
                    tahunAjaranAktif={setting?.tahunAjaranAktif || '2026/2027'}
                    currentUserLevel={currentUser?.levelUser}
                    currentUsername={currentUser?.username}
                    onCreateBeasiswa={handleCreateBeasiswa}
                    onVerifikasiBeasiswa={handleVerifikasiBeasiswa}
                    onBatchVerifikasiBeasiswa={handleBatchVerifikasiBeasiswa}
                    onDeleteBeasiswa={handleDeleteBeasiswa}
                    onRefresh={async () => { await loadAllData(true); }}
                  />
                } />

                <Route path="/tagihan-siswa" element={
                  <TagihanSiswaView
                    tagihanList={tagihanList}
                    siswaList={siswaList}
                    posList={posList}
                    kelasList={kelasList}
                    onGenerateTagihan={handleGenerateTagihan}
                    onDeleteTagihan={handleDeleteTagihan}
                    onNavigateToRecurring={() => navigate('/tagihan-spp-berkala')}
                  />
                } />

                <Route path="/tagihan-spp-berkala" element={
                  <RecurringSppBillingView
                    siswaList={siswaList}
                    kelasList={kelasList}
                    posList={posList}
                    sppRules={sppRules}
                    tagihanList={tagihanList}
                    tahunAjaranAktif={setting?.tahunAjaranAktif || '2026/2027'}
                    onCreateRule={handleCreateSppRule}
                    onUpdateRule={handleUpdateSppRule}
                    onGenerateRecurringSpp={handleGenerateRecurringSpp}
                    onNavigateToTagihan={() => navigate('/tagihan-siswa')}
                  />
                } />

                <Route path="/setting" element={
                  setting && <SettingTahunAjaranView setting={setting} onUpdateSetting={handleUpdateSetting} appMode={appMode} setAppMode={setAppMode} />
                } />

                {/* Transaksi Sub-Menus */}
                <Route path="/loket" element={
                  <LoketPembayaranView
                    siswaList={siswaList}
                    tagihanList={tagihanList}
                    currentUser={currentUser}
                    onProcessPayment={handleProcessPayment}
                    onShowReceipt={(trx) => setActiveReceipt(trx)}
                  />
                } />

                <Route path="/cetak-bukti" element={
                  <CetakBuktiCancelView
                    transaksiList={transaksiList}
                    onCancelTransaksi={handleCancelTransaksi}
                    onShowReceipt={(trx) => setActiveReceipt(trx)}
                  />
                } />

                <Route path="/saldo-siswa" element={
                  <SaldoSiswaView
                    siswaList={siswaList}
                    kelasList={kelasList}
                    onDepositOrWithdraw={handleDepositOrWithdraw}
                  />
                } />

                <Route path="/transaksi-siswa" element={
                  <TransaksiSiswaView
                    transaksiList={transaksiList}
                    siswaList={siswaList}
                    onShowReceipt={(trx) => setActiveReceipt(trx)}
                  />
                } />

                {/* Report Sub-Menus */}
                <Route path="/report/tagihan" element={
                  <ReportView
                    activeReportType="Report Tagihan"
                    tagihanList={tagihanList}
                    transaksiList={transaksiList}
                    siswaList={siswaList}
                    kelasList={kelasList}
                    posList={posList}
                    tahunAjaranList={tahunAjaranList}
                  />
                } />
                <Route path="/report/pembayaran" element={
                  <ReportView
                    activeReportType="Report Pembayaran"
                    tagihanList={tagihanList}
                    transaksiList={transaksiList}
                    siswaList={siswaList}
                    kelasList={kelasList}
                    posList={posList}
                    tahunAjaranList={tahunAjaranList}
                    currentUser={currentUser}
                  />
                } />
                <Route path="/report/tunggakan" element={
                  <ReportView
                    activeReportType="Report Tunggakan"
                    tagihanList={tagihanList}
                    transaksiList={transaksiList}
                    siswaList={siswaList}
                    kelasList={kelasList}
                    posList={posList}
                    tahunAjaranList={tahunAjaranList}
                  />
                } />
                <Route path="/report/jurnal-umum" element={
                  <JurnalAkuntansiView />
                } />
                <Route path="/report/penerimaan" element={
                  <ReportView
                    activeReportType="Report Penerimaan"
                    tagihanList={tagihanList}
                    transaksiList={transaksiList}
                    siswaList={siswaList}
                    kelasList={kelasList}
                    posList={posList}
                    tahunAjaranList={tahunAjaranList}
                  />
                } />

                {/* Utility Sub-Menus */}
                <Route path="/utility/naik-kelas" element={
                  <UtilityView
                    activeUtilityType="Naik Kelas"
                    siswaList={siswaList}
                    kelasList={kelasList}
                    userPrivilegeList={userPrivilegeList}
                    onPromoteClass={handlePromoteClass}
                    onGraduateClass={handleGraduateClass}
                    onAddPrivilege={handleAddUserPrivilege}
                    onUpdatePrivilege={handleUpdateUserPrivilege}
                    onDeletePrivilege={handleDeleteUserPrivilege}
                  />
                } />
                <Route path="/utility/kelulusan" element={
                  <UtilityView
                    activeUtilityType="Kelulusan"
                    siswaList={siswaList}
                    kelasList={kelasList}
                    userPrivilegeList={userPrivilegeList}
                    onPromoteClass={handlePromoteClass}
                    onGraduateClass={handleGraduateClass}
                    onAddPrivilege={handleAddUserPrivilege}
                    onUpdatePrivilege={handleUpdateUserPrivilege}
                    onDeletePrivilege={handleDeleteUserPrivilege}
                  />
                } />
                <Route path="/utility/user-privilege" element={
                  currentUser?.levelUser === 'SUPERADMIN' ? (
                    <UtilityView
                      activeUtilityType="User Privilege"
                      siswaList={siswaList}
                      kelasList={kelasList}
                      userPrivilegeList={userPrivilegeList}
                      onPromoteClass={handlePromoteClass}
                      onGraduateClass={handleGraduateClass}
                      onAddPrivilege={handleAddUserPrivilege}
                      onUpdatePrivilege={handleUpdateUserPrivilege}
                      onDeletePrivilege={handleDeleteUserPrivilege}
                    />
                  ) : (
                    <Navigate to="/" replace />
                  )
                } />
                <Route path="/utility/activity-log" element={
                  <UtilityView
                    activeUtilityType="Activity Log"
                    siswaList={siswaList}
                    kelasList={kelasList}
                    userPrivilegeList={userPrivilegeList}
                    onPromoteClass={handlePromoteClass}
                    onGraduateClass={handleGraduateClass}
                    onAddPrivilege={handleAddUserPrivilege}
                    onUpdatePrivilege={handleUpdateUserPrivilege}
                    onDeletePrivilege={handleDeleteUserPrivilege}
                  />
                } />
                <Route path="/utility/backup" element={
                  currentUser?.levelUser === 'SUPERADMIN' ? (
                    <UtilityView
                      activeUtilityType="Backup & Restore"
                      siswaList={siswaList}
                      kelasList={kelasList}
                      userPrivilegeList={userPrivilegeList}
                      onPromoteClass={handlePromoteClass}
                      onGraduateClass={handleGraduateClass}
                      onAddPrivilege={handleAddUserPrivilege}
                      onUpdatePrivilege={handleUpdateUserPrivilege}
                      onDeletePrivilege={handleDeleteUserPrivilege}
                    />
                  ) : (
                    <Navigate to="/" replace />
                  )
                } />

                {/* Tabungan Sub-Menus */}
                <Route path="/tabungan" element={
                  <DashboardTabunganView
                    siswaList={siswaList}
                  />
                } />
                <Route path="/tabungan/mutasi" element={
                  <MutasiTabunganView
                    siswaList={siswaList}
                    kelasList={kelasList}
                    currentUser={currentUser}
                  />
                } />

                {/* E-Wallet */}
                <Route path="/ewallet" element={
                  <DashboardEWalletView siswaList={siswaList} />
                } />

                <Route path="/report/tabungan" element={
                  <ReportView
                    activeReportType="Report Tabungan"
                    tagihanList={tagihanList}
                    transaksiList={transaksiList}
                    siswaList={siswaList}
                    kelasList={kelasList}
                    posList={posList}
                    tahunAjaranList={tahunAjaranList}
                  />
                } />

                {/* Payment Gateway Console Quick Access */}
                <Route path="/payment-gateway" element={
                  <GatewayConsoleView />
                } />

                {/* Dokumentasi & Buku Panduan Resmi */}
                <Route path="/panduan" element={<DocumentationView />} />
                <Route path="/docs" element={<DocumentationView />} />
              </Routes>
            )}
          </>
        )}
      </main>

      {/* Printable Receipt Modal */}
      {activeReceipt && (
        <ReceiptModal transaction={activeReceipt} onClose={() => setActiveReceipt(null)} />
      )}

      {/* Modern Compact Footer */}
      <footer className="bg-white border-t border-slate-200 py-3 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center gap-2">
          <span>
            © 2026 SD Muhammadiyah Terpadu Ponorogo • Sistem Pembayaran Siswa Terpadu
          </span>
        </div>
      </footer>
      </div>
    </div>
  );
}
